import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AppConstants } from '../AppConstants';
import { AuthService } from '../service/auth.service';
import { DashboardService } from './dashboard.service';
import { buildDashboard, customerAmount, DashboardRevenueData, DashboardTransaction, indiaDate, RevenueBreakdown, RevenuePeriod, slotsOf } from './dashboard-data';

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.css']
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('bookingDetails') bookingDetails: TemplateRef<any>;
  data: ReturnType<typeof buildDashboard>;
  loading = false;
  error = '';
  tenantId: number;
  roleId: number;
  now = new Date();
  get expiryEndDate(): Date { return indiaDate(this.now).add(3, 'months').toDate(); }
  updatedAt: Date;
  monthlyRevenue: RevenuePeriod[] = [];
  monthlyRevenueBreakdown: RevenueBreakdown[] = [];
  private monthlyRevenuePeriods: RevenuePeriod[] = [];
  monthlyRevenueLoading = false;
  monthlyRevenueError = '';
  monthlyRevenueChartMax = 1;
  revenueProducts: any[] = [];
  get revenueProductTypes(): string[] {
    return Array.from(new Set(this.revenueProducts.map(product => product.productType).filter(Boolean))).sort();
  }
  revenueVenues: any[] = [];
  revenueProductId: number | null = null;
  revenueProductType = '';
  revenueVenueId: number | null = null;
  revenueFilterRequests: Subscription[] = [];
  pendingPage = 0;
  expiryPage = 0;
  selected: DashboardTransaction;
  customerAmount = customerAmount;
  slotsOf = slotsOf;
  private request: Subscription;
  private monthlyRevenueRequest: Subscription;
  private bookingDialog: MatDialogRef<any>;

  constructor(private dashboard: DashboardService, private auth: AuthService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.auth.syncUserContext();
    const current = JSON.parse(this.auth.getStoredItem(AppConstants.SESSION_STORAGE_CURRENT_USER_CONTEXT) || '{}');
    const external = this.auth.getStoredItem(AppConstants.SESSION_STORAGE_EXTERNAL_USER_CONTEXT);
    this.roleId = Number(external ? JSON.parse(external).roleId : current.roleId);
    this.tenantId = this.roleId === 1 ? Number(AppConstants.SESSION_STORAGE_TENANTID) : Number(current.tenantId);
    if (![1, 2].includes(this.roleId) || !this.tenantId) {
      this.error = 'Select a business to view its dashboard.';
      return;
    }
    this.revenueFilterRequests.push(this.auth.getAllProductsByTenantId(this.tenantId, true).subscribe((items: any[]) => {
      this.revenueProducts = Array.isArray(items) ? items : [];
    }));
    this.revenueFilterRequests.push(this.auth.getAllVenuesByTenantId(this.tenantId).subscribe((items: any[]) => {
      this.revenueVenues = Array.isArray(items) ? items : [];
    }));
    this.refresh();
  }

  refresh(): void {
    if (this.loading || !this.tenantId) return;
    this.loadMonthlyRevenue();
    this.loading = true;
    this.error = '';
    this.data = null;
    this.request = this.dashboard.load(this.tenantId, this.roleId).subscribe({
      next: response => {
        this.now = new Date();
        this.data = response;
        this.updatedAt = this.now;
        this.pendingPage = this.expiryPage = 0;
        this.loading = false;
      },
      error: (error: Error) => {
        this.error = error.message || 'Dashboard data could not be loaded. Please try again.';
        this.loading = false;
      }
    });
  }

  loadMonthlyRevenue(): void {
    this.monthlyRevenueRequest?.unsubscribe();
    this.monthlyRevenueLoading = true;
    this.monthlyRevenueError = '';
    this.monthlyRevenueRequest = this.dashboard.loadMonthlyRevenue(this.tenantId).subscribe({
      next: (response: DashboardRevenueData) => {
        this.monthlyRevenuePeriods = response.buckets;
        this.monthlyRevenueBreakdown = response.breakdown;
        this.applyMonthlyRevenueFilters();
        this.monthlyRevenueLoading = false;
      },
      error: (error: Error) => {
        this.monthlyRevenueError = error.message || 'Monthly revenue could not be loaded.';
        this.monthlyRevenueLoading = false;
      }
    });
  }

  applyMonthlyRevenueFilters(): void {
    this.monthlyRevenue = this.monthlyRevenuePeriods.map(period => {
      const matching = this.monthlyRevenueBreakdown.filter(row => row.key === period.key
        && (!this.revenueProductId || row.productId === this.revenueProductId)
        && (!this.revenueProductType || row.productType.toLowerCase() === this.revenueProductType.toLowerCase())
        && (!this.revenueVenueId || row.venueId === this.revenueVenueId));
      return {
        ...period,
        totalRevenue: matching.reduce((total, row) => total + row.totalRevenue, 0),
        cancelled: matching.reduce((total, row) => total + row.cancelled, 0)
      };
    });
    this.monthlyRevenueChartMax = Math.max(1, ...this.monthlyRevenue.map(row => row.totalRevenue + row.cancelled));
  }

  openBooking(row: DashboardTransaction): void {
    this.selected = row;
    this.bookingDialog = this.dialog.open(this.bookingDetails, { width: '650px', maxWidth: '95vw', maxHeight: '90vh', ariaLabelledBy: 'dashboard-booking-title' });
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.monthlyRevenueRequest?.unsubscribe();
    this.revenueFilterRequests.forEach(request => request.unsubscribe());
    this.bookingDialog?.close();
  }
}
